<!-- Copyright (c) 2024 Digital Systems (M) Sdn. Bhd. -->

<template>
  <div class="print-bank-recon-main">
    <div class="report-preview">
      <DesignerV3
        v-if="showDesignerV3"
        :reportFiles="designerV3ReportFiles"
        :reportData="designerV3ReportData"
        :canToggleBackground="false"
        :canShare="true"
        :canClose="false"
        ref="designerV3"
      />
    </div>
  </div>
</template>

<script>
export default {
  components: {},
  computed: {},
  data: function () {
    return {
      selectedStatementName: '',
      selectedStatementDate: '',

      showDesignerV3: false,
      designerV3ReportFiles: [],
      designerV3ReportData: [],
      // designerV3ShareBehaviour: {
      //   behaviour: "uploadAndPopup",
      //   options: {
      //     fileName: `BankReconciliationReport_${new Date().formatString("ddMMyyyy_HHmmss")}.pdf`,
      //   },
      // },
    };
  },
  props: {},
  methods: {
    previewBankReconciliationReport: async function () {
      const requestBody = {
        statementname: this.selectedStatementName,
        statementdate: this.selectedStatementDate,
        bankcode: this.selectedStatementBankCode,
      };
      //console.log(requestBody)

      this.$fullscreenLoading('Getting Bank Statement Details');
      const statementDetails = await this.$apiCallV2('/BankReconciliation/PrintBankStatementDetails', requestBody);
      //console.log(statementDetails)

      this.$fullscreenLoading('Preparing to Print');

      let grandTotal = 0;
      let reconAsPerBankStatement = 0;
      let reconAsPerActualBankStatement = 0;
      let totalUnPresented = 0;
      let totalNotClear = 0;
      let totalDebitedinBank = 0;
      let totalCreditedinBank = 0;
      const currentDateTime = new Date().formatString('dd/MM/yyyy HH:mm:ss');

      //#region All Total Calculations
      //Cheque/Payment cleared
      if (statementDetails.BankStatement[0].ChequePaymentClearedTotal !== undefined) {
        let totalValue = statementDetails.BankStatement[0].ChequePaymentClearedTotal.filter(
          (item) => item.TotalChequePaymentCleared !== null,
        ).reduce((sum, v) => sum + Math.abs(parseFloat(v.TotalChequePaymentCleared)), 0);
        totalValue = totalValue.toFixed(2);

        // Check if the original total was negative
        const sumTotalCleared = statementDetails.BankStatement[0].ChequePaymentClearedTotal.filter(
          (item) => item.TotalChequePaymentCleared !== null,
        ) // Filter out null values
          .reduce((sum, v) => sum + parseFloat(v.TotalChequePaymentCleared), 0); // Calculate sum

        // Format as negative with parentheses if it was negative
        const isNegative = sumTotalCleared < 0;
        if (isNegative) {
          statementDetails.BankStatement[0].ChequePaymentClearedTotal = `(${this.formatNumber(totalValue)})`;
          grandTotal += parseFloat(`-${totalValue}`);
          reconAsPerBankStatement += parseFloat(`-${totalValue}`);
        } else {
          statementDetails.BankStatement[0].ChequePaymentClearedTotal = this.formatNumber(totalValue);
          grandTotal += parseFloat(totalValue);
          reconAsPerBankStatement += parseFloat(totalValue);
        }
        //console.log('calculated Cheque/Payment cleared: ' + statementDetails.BankStatement[0].ChequePaymentClearedTotal);
      }

      //calculate total of Unpresented cheque
      if (statementDetails.BankStatement[0].UnpresentedChequeTotal !== undefined) {
        let totalValue = statementDetails.BankStatement[0].UnpresentedChequeTotal.filter(
          (item) => item.TotalUnpresentedCheque !== null,
        ).reduce((sum, v) => sum + Math.abs(parseFloat(v.TotalUnpresentedCheque)), 0);
        totalValue = totalValue.toFixed(2);

        // Check if the original total was negative
        const sumTotalCleared = statementDetails.BankStatement[0].UnpresentedChequeTotal.filter(
          (item) => item.TotalUnpresentedCheque !== null,
        ) // Filter out null values
          .reduce((sum, v) => sum + parseFloat(v.TotalUnpresentedCheque), 0); // Calculate sum

        // Format as negative with parentheses if it was negative
        const isNegative = sumTotalCleared < 0;
        if (isNegative) {
          statementDetails.BankStatement[0].UnpresentedChequeTotal = `(${this.formatNumber(totalValue)})`;
          grandTotal += parseFloat(`-${totalValue}`);
          totalUnPresented += parseFloat(`-${totalValue}`);
        } else {
          statementDetails.BankStatement[0].UnpresentedChequeTotal = this.formatNumber(totalValue);
          grandTotal += parseFloat(totalValue);
          totalUnPresented += parseFloat(totalValue);
        }
        //console.log('calculated Unpresented cheque: ' + statementDetails.BankStatement[0].UnpresentedChequeTotal);
      }

      //calculate total of Deposit cleared
      if (statementDetails.BankStatement[0].DepositClearedTotal !== undefined) {
        let totalValue = statementDetails.BankStatement[0].DepositClearedTotal.filter(
          (item) => item.TotalDepositCleared !== null,
        ).reduce((sum, v) => sum + Math.abs(parseFloat(v.TotalDepositCleared)), 0);
        totalValue = totalValue.toFixed(2);

        // Check if the original total was negative
        const sumTotalCleared = statementDetails.BankStatement[0].DepositClearedTotal.filter(
          (item) => item.TotalDepositCleared !== null,
        ) // Filter out null values
          .reduce((sum, v) => sum + parseFloat(v.TotalDepositCleared), 0); // Calculate sum

        // Format as negative with parentheses if it was negative
        const isNegative = sumTotalCleared < 0;
        if (isNegative) {
          statementDetails.BankStatement[0].DepositClearedTotal = `(${this.formatNumber(totalValue)})`;
          grandTotal += parseFloat(`-${totalValue}`);
          reconAsPerBankStatement += parseFloat(`-${totalValue}`);
        } else {
          statementDetails.BankStatement[0].DepositClearedTotal = this.formatNumber(totalValue);
          grandTotal += parseFloat(totalValue);
          reconAsPerBankStatement += parseFloat(totalValue);
        }
        //console.log('calculated Deposit cleared: ' + statementDetails.BankStatement[0].DepositClearedTotal);
      }

      //calculate total of Uncleared Deposits
      if (statementDetails.BankStatement[0].UnclearedDepositsTotal !== undefined) {
        let totalValue = statementDetails.BankStatement[0].UnclearedDepositsTotal.filter(
          (item) => item.TotalUnclearedDeposits !== null,
        ).reduce((sum, v) => sum + Math.abs(parseFloat(v.TotalUnclearedDeposits)), 0);
        totalValue = totalValue.toFixed(2);

        // Check if the original total was negative
        const sumTotalCleared = statementDetails.BankStatement[0].UnclearedDepositsTotal.filter(
          (item) => item.TotalUnclearedDeposits !== null,
        ) // Filter out null values
          .reduce((sum, v) => sum + parseFloat(v.TotalUnclearedDeposits), 0); // Calculate sum

        // Format as negative with parentheses if it was negative
        const isNegative = sumTotalCleared < 0;
        if (isNegative) {
          statementDetails.BankStatement[0].UnclearedDepositsTotal = `(${this.formatNumber(totalValue)})`;
          grandTotal += parseFloat(`-${totalValue}`);
          totalNotClear += parseFloat(`-${totalValue}`);
        } else {
          statementDetails.BankStatement[0].UnclearedDepositsTotal = this.formatNumber(totalValue);
          grandTotal += parseFloat(totalValue);
          totalNotClear += parseFloat(totalValue);
        }
        //console.log('calculated Uncleared Deposits: ' + statementDetails.BankStatement[0].UnclearedDepositsTotal);
      }

      //calculate total of Debited in Bank not in Cash book
      if (statementDetails.BankStatement[0].InBankTotal !== undefined) {
        let totalValue = statementDetails.BankStatement[0].InBankTotal.filter(
          (item) => item.TotalDebitedBank !== null,
        ).reduce((sum, v) => sum + Math.abs(parseFloat(v.TotalDebitedBank)), 0);
        totalValue = totalValue.toFixed(2);

        // Check if the original total was negative
        const sumTotalCleared = statementDetails.BankStatement[0].InBankTotal.filter(
          (item) => item.TotalDebitedBank !== null,
        ) // Filter out null values
          .reduce((sum, v) => sum + parseFloat(v.TotalDebitedBank), 0); // Calculate sum

        // Format as negative with parentheses if it was negative
        const isNegative = sumTotalCleared < 0;
        if (isNegative) {
          statementDetails.BankStatement[0].InBankTotal = `(${this.formatNumber(totalValue)})`;
          grandTotal += parseFloat(`-${totalValue}`);
          reconAsPerBankStatement += parseFloat(`-${totalValue}`);
          totalDebitedinBank += parseFloat(`-${totalValue}`);
        } else {
          statementDetails.BankStatement[0].InBankTotal = this.formatNumber(totalValue);
          grandTotal += parseFloat(totalValue);
          reconAsPerBankStatement += parseFloat(totalValue);
          totalDebitedinBank += parseFloat(totalValue);
        }
        //console.log('calculated InBank Debited: ' + statementDetails.BankStatement[0].InBankTotal);

        const hasZeroGL = statementDetails.BankStatement[0].InBank.some((record) => record.GL === 0);
        if (hasZeroGL) statementDetails.BankStatement[0].showGLSummary = 'Y';
      }
      //calculate total of Credited in Bank not in Cash book
      if (statementDetails.BankStatement[0].InBankTotal2 !== undefined) {
        let totalValue = statementDetails.BankStatement[0].InBankTotal2.filter(
          (item) => item.TotalCreditedBank !== null,
        ).reduce((sum, v) => sum + Math.abs(parseFloat(v.TotalCreditedBank)), 0);
        totalValue = totalValue.toFixed(2);

        // Check if the original total was negative
        const sumTotalCleared = statementDetails.BankStatement[0].InBankTotal2.filter(
          (item) => item.TotalCreditedBank !== null,
        ) // Filter out null values
          .reduce((sum, v) => sum + parseFloat(v.TotalCreditedBank), 0); // Calculate sum

        // Format as negative with parentheses if it was negative
        const isNegative = sumTotalCleared < 0;
        if (isNegative) {
          statementDetails.BankStatement[0].InBankTotal2 = `(${this.formatNumber(totalValue)})`;
          grandTotal += parseFloat(`-${totalValue}`);
          reconAsPerBankStatement += parseFloat(`-${totalValue}`);
          totalCreditedinBank += parseFloat(`-${totalValue}`);
        } else {
          statementDetails.BankStatement[0].InBankTotal2 = this.formatNumber(totalValue);
          grandTotal += parseFloat(totalValue);
          reconAsPerBankStatement += parseFloat(totalValue);
          totalCreditedinBank += parseFloat(totalValue);
        }
        //console.log('calculated InBank2 Credited: ' + statementDetails.BankStatement[0].InBankTotal2);

        const hasZeroGL = statementDetails.BankStatement[0].InBank2.some((record) => record.GL === 0);
        if (hasZeroGL) statementDetails.BankStatement[0].showGLSummary = 'Y';
      }
      //#endregion

      //#region Statement Summary
      const balBF = parseFloat(statementDetails.BankStatement[0].BalBF);
      const balCF = parseFloat(statementDetails.BankStatement[0].BalCF);
      const unreconCredit = parseFloat(statementDetails.BankStatement[0].UnreconCredit);
      const unreconDebit = parseFloat(statementDetails.BankStatement[0].UnreconDebit);
      //Add up grand total
      //console.log('grandTotal: ' + grandTotal)
      if (!isNaN(grandTotal)) {
        //console.log('balBF:' +balBF)
        grandTotal += balBF;
        //console.log('totalinGL: ' + grandTotal)
        if (grandTotal < 0) {
          statementDetails.BankStatement[0].TotalinGL = `(${this.formatNumber(Math.abs(grandTotal))})`;
        } else {
          statementDetails.BankStatement[0].TotalinGL = this.formatNumber(grandTotal);
        }
      }
      //Reconciliation as per bank statement
      //console.log('Reconciliation as per bank statement: ' + reconAsPerBankStatement)
      if (!isNaN(reconAsPerBankStatement)) {
        reconAsPerBankStatement += balBF;
        //console.log('totalinGL: ' + reconAsPerBankStatement)
        //for reconciliation as per bank statement c/f
        if (reconAsPerBankStatement < 0) {
          statementDetails.BankStatement[0].TotalAsPerBankStatement = `(${this.formatNumber(Math.abs(reconAsPerBankStatement))})`;
        } else {
          statementDetails.BankStatement[0].TotalAsPerBankStatement = this.formatNumber(reconAsPerBankStatement);
        }

        const unreconCredit = parseFloat(statementDetails.BankStatement[0].UnreconCredit);
        const unreconDebit = parseFloat(statementDetails.BankStatement[0].UnreconDebit);

        //for reconciliation as per bank statement (add unrecon credit and debit to match with actual bank statement closing balance)
        reconAsPerActualBankStatement = reconAsPerBankStatement + unreconCredit + unreconDebit;
        if (reconAsPerActualBankStatement < 0) {
          statementDetails.BankStatement[0].TotalAsPerActualBankStatement = `(${this.formatNumber(Math.abs(reconAsPerActualBankStatement))})`;
        } else {
          statementDetails.BankStatement[0].TotalAsPerActualBankStatement =
            this.formatNumber(reconAsPerActualBankStatement);
        }
      }
      //Less: Uncleared Deposit
      statementDetails.BankStatement[0].UnclearedDepositsTotal_Summary =
        statementDetails.BankStatement[0].UnclearedDepositsTotal ?
          statementDetails.BankStatement[0].UnclearedDepositsTotal
        : '0.00';
      //Less: Unpresented Cheque
      statementDetails.BankStatement[0].UnpresentedChequeTotal_Summary =
        statementDetails.BankStatement[0].UnpresentedChequeTotal ?
          statementDetails.BankStatement[0].UnpresentedChequeTotal
        : '0.00';
      //Statement Balance at at Statement date
      if (!isNaN(reconAsPerActualBankStatement) && !isNaN(totalUnPresented) && !isNaN(totalNotClear)) {
        const total = reconAsPerActualBankStatement + totalUnPresented + totalNotClear;
        //console.log('Statement Balance at at Statement date: ' + total)
        if (total < 0) {
          statementDetails.BankStatement[0].StatementBalance = `(${this.formatNumber(Math.abs(total))})`;
        } else {
          statementDetails.BankStatement[0].StatementBalance = this.formatNumber(total);
        }
        //GL section - only show if GL = 0
        statementDetails.BankStatement[0].InBankTotal2_Summary = `(${this.formatNumber(Math.abs(totalCreditedinBank))})`;
        statementDetails.BankStatement[0].InBankTotal_Summary = `(${this.formatNumber(Math.abs(totalDebitedinBank))})`;
        //GL balance
        const GLtotal = total + totalDebitedinBank + totalCreditedinBank;
        if (GLtotal < 0) {
          statementDetails.BankStatement[0].GLtotal = `(${this.formatNumber(Math.abs(GLtotal))})`;
        } else {
          statementDetails.BankStatement[0].GLtotal = this.formatNumber(GLtotal);
        }
      }

      //#endregion

      // Format balBF as negative with parentheses if it was negative
      if (balBF < 0) {
        statementDetails.BankStatement[0].BalBF = `(${this.formatNumber(Math.abs(balBF).toFixed(2))})`;
      } else {
        statementDetails.BankStatement[0].BalBF = this.formatNumber(balBF);
      }
      // Format balCF as negative with parentheses if it was negative
      if (balCF < 0) {
        statementDetails.BankStatement[0].BalCF = `(${this.formatNumber(Math.abs(balCF).toFixed(2))})`;
      } else {
        statementDetails.BankStatement[0].BalCF = this.formatNumber(balCF);
      }
      // Format unReconDebit and unReconCredit with parentheses if it was negative
      if (unreconCredit < 0) {
        statementDetails.BankStatement[0].UnreconCredit = `(${this.formatNumber(Math.abs(unreconCredit).toFixed(2))})`;
      } else {
        statementDetails.BankStatement[0].UnreconCredit = this.formatNumber(unreconCredit);
      }
      if (unreconDebit < 0) {
        statementDetails.BankStatement[0].UnreconDebit = `(${this.formatNumber(Math.abs(unreconDebit).toFixed(2))})`;
      } else {
        statementDetails.BankStatement[0].UnreconDebit = this.formatNumber(unreconDebit);
      }
      //top right info
      statementDetails.BankStatement[0].currentDateTime = currentDateTime;
      //statementDetails.BankStatement[0].Page =  "Page {{jsreportPageNum}} of {{jsreportTotalPage}}"; - moved into report template

      this.$fullscreenLoading();
      this.designerV3ReportFiles = ['BankReconciliationReport.json'];

      this.designerV3ReportData = statementDetails.BankStatement;
      this.showDesignerV3 = true;

      // //For testing
      // console.log(statementDetails.BankStatement)
      // // Generate Excel file using dataToDownload
      // this.$fullscreenLoading('Generating Excel');
      // //await this.$jsonToExcel(`${this.selectedStatementName} ${currentDateTime}`, this.convertDataToDownload(statementDetails.BankStatement), "xlsx", {});
      // //await this.$jsonToExcel(`ChequePaymentCleared`, this.convertDataToDownload(statementDetails.BankStatement[0].ChequePaymentCleared), "xlsx", {});
      // //await this.$jsonToExcel(`UnclearedDeposits`, this.convertDataToDownload(statementDetails.BankStatement[0].UnclearedDeposits), "xlsx", {});
      // //await this.$jsonToExcel(`UnpresentedCheque`, this.convertDataToDownload(statementDetails.BankStatement[0].UnpresentedCheque), "xlsx", {});
      // this.$fullscreenLoading();
    },
    // convertDataToDownload: function(item) {
    //   let dataInString = JSON.stringify(item);
    //   dataInString = this.removeTagWhenDownload(dataInString);
    //   return JSON.parse(dataInString);
    // },
    // removeTagWhenDownload: function(item) {
    //   item = item.replace(/(<)(.*?)(>)/g, '');
    //   return item;
    // },
    // fixed to be formatted #,##0.00 (3 digits per commma).
    // not using $currencyFormat because there can be a huge number of bank statements, which can cause performance issues.
    formatNumber: function (value) {
      const numericValue = parseFloat(value);
      return numericValue.toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,');
    },
  },
  mounted() {
    if (this.$route.query['statement']) {
      this.selectedStatementName = this.$route.query['statement'];
      this.selectedStatementDate = this.$route.query['date'];
      this.selectedStatementBankCode = this.$route.query['bank'];

      this.previewBankReconciliationReport();
    }
  },
  watch: {},
};
</script>

<style lang="scss" scoped>
@import '@/css/all.scss';

.print-bank-recon-main {
  font-size: 12px;
  width: 100%;
  height: 100%;
  padding: 10px 45px;
  display: flex;

  > .report-preview {
    margin-left: 2.5px;
    flex: 1;
    background-color: white;
    padding: 5px;
  }
}
</style>
